import { logger } from '@curology/logger/client';

import { type Brand } from '@/consts/brands';
import { INELIGIBILITY_REASONS } from '@/consts/eligibility';
import { type HairLossLocation } from '@/consts/hairLossLocation';
import { type ObgynStatus } from '@/consts/obgynStatus';
import { type TreatmentCategory } from '@/consts/treatmentCategories';
import { saveProductEligibility } from '@/services/eligibilityService/actions';
import { trackEligibilitySubmitted } from '@/services/eligibilityService/useEligibility';
import {
  type IneligibilityReasonType,
  type ProductEligibilityDataType,
} from '@/types/eligibility/eligibility';
import { urls } from '@/utils/urls';

enum BAILOUT_ROUTES {
  default = 'default',
  ineligible = 'ineligible',
}

const ROUTES: Record<
  BAILOUT_ROUTES,
  (treatmentCategory: TreatmentCategory, brand: Brand) => string
> = {
  [BAILOUT_ROUTES.default]: (_, brand) => urls[brand].site.url,
  [BAILOUT_ROUTES.ineligible]: (treatmentCategory) =>
    urls.app.ineligible(treatmentCategory),
} as const;

const BAIL_OUT_SEMANTIC_MEANINGS = ['obgyn', 'hair-loss-location'];

const productErrorKeys: string[] = [
  INELIGIBILITY_REASONS.HAIR_LOSS_LOCATION,
  INELIGIBILITY_REASONS.OBGYN,
];

const addressErrorKey: string =
  INELIGIBILITY_REASONS.HAIR_UNDELIVERABLE_ADDRESS;

const isBailOutSemanticMeaning = (semanticMeaning: string[]) => {
  return BAIL_OUT_SEMANTIC_MEANINGS.some((meaning) =>
    semanticMeaning.includes(meaning),
  );
};

function formatReason(
  reason: IneligibilityReasonType,
): IneligibilityReasonType {
  switch (true) {
    case INELIGIBILITY_REASONS.NONE === reason:
    case addressErrorKey === reason:
      return reason;
    case productErrorKeys.includes(reason):
      return INELIGIBILITY_REASONS.HAIR_CANNOT_RECEIVE_PRODUCT;
    default:
      return INELIGIBILITY_REASONS.UNKNOWN;
  }
}

function isEligible(reason: IneligibilityReasonType) {
  return reason === INELIGIBILITY_REASONS.NONE;
}

async function getIneligibleReason(
  data: ProductEligibilityDataType,
): Promise<IneligibilityReasonType> {
  try {
    const productEligibilityResponse = await saveProductEligibility(data);
    if (productEligibilityResponse?.ineligibleReason) {
      return productEligibilityResponse.ineligibleReason
        .reason as IneligibilityReasonType;
    }
  } catch (error: any) {
    logger.log(`Error updating product eligibility: ${error.message}`);
  }

  return INELIGIBILITY_REASONS.NONE;
}

interface EvaluateProductEligibilityType {
  ineligibleReason: IneligibilityReasonType;
  shouldBail: boolean;
}

const getBailout = async (
  treatmentCategory: TreatmentCategory,
  question: any,
  brand: Brand,
): Promise<string | null> => {
  const productEligibility = await evaluateProductEligibility(
    treatmentCategory,
    question.getPropertyValue('semanticMeaning'),
    question.value,
    brand,
  );

  return productEligibility.shouldBail
    ? bailOutRoute(BAILOUT_ROUTES.ineligible, brand, treatmentCategory)
    : null;
};

const evaluateProductEligibility = async (
  treatmentCategories: TreatmentCategory,
  semanticMeaning: string[],
  values: ObgynStatus | HairLossLocation[],
  brand: Brand,
): Promise<EvaluateProductEligibilityType> => {
  if (semanticMeaning.indexOf('hair-loss-location') > -1) {
    const ineligibleReason = await getIneligibleReason({
      hairLossLocation: values as HairLossLocation[],
      treatmentCategories: treatmentCategories,
      brand,
    });
    trackEligibilitySubmitted(brand, treatmentCategories);
    return {
      ineligibleReason: formatReason(ineligibleReason),
      shouldBail: ineligibleReason === INELIGIBILITY_REASONS.HAIR_LOSS_LOCATION,
    };
  }

  if (semanticMeaning.indexOf('obgyn') > -1) {
    const ineligibleReason = await getIneligibleReason({
      obgynStatus: values as ObgynStatus,
      treatmentCategories: treatmentCategories,
      brand,
    });
    trackEligibilitySubmitted(brand, treatmentCategories);
    return {
      ineligibleReason: formatReason(ineligibleReason),
      shouldBail: !isEligible(ineligibleReason),
    };
  }
  return {
    ineligibleReason: INELIGIBILITY_REASONS.NONE,
    shouldBail: false,
  };
};

const bailOutRoute = (
  route: keyof typeof ROUTES,
  brand: Brand,
  treatmentCategory: TreatmentCategory,
) => {
  return (
    ROUTES[route]?.(treatmentCategory, brand) ??
    ROUTES.default(treatmentCategory, brand)
  );
};

export {
  isBailOutSemanticMeaning,
  BAILOUT_ROUTES,
  bailOutRoute,
  evaluateProductEligibility,
  getBailout,
};
